import {Alert, Icon, Modal, Select, Spin} from 'antd';
import autobind from 'autobind-decorator';
import * as React from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {connect} from 'redux-scaffolding-ts';
import {formatMessage} from 'src/core/services/http.service';
import {NewProjectTasksFromTemplates} from 'src/stores/projecttasks-store';
import {Query} from '../../../core/stores/dataStore';
import {TableModel, TableView} from '../../../core/ui/collections/table';
import {TaskTemplatesStore, TaskTemplateSummary} from '../../../stores/templates-store';
import ProjectTemplatesFilter from './tasks/project-template-filter';
import ProjectTemplatesColumn from './tasks/project-templates-column';
import {NewProjectTaskAssignmentItem, NewProjectTaskAssignmentStore} from 'src/stores/projecttaskassignments-store';

interface ApplyTemplateViewProps extends WithTranslation {
    companyId: string,
    projectId: string,
    onClose: () => void;
}

interface ApplyTemplateViewState {
    taskTemplatesQuery: Query,
    selected: any[],
    isBusy: boolean,
    selectedRoles: string[];
    employeesIds: string[];
}

const roles = [
    "ClientAdmin",
    "Manager",
    "User",
];

@connect(["taskTemplates", TaskTemplatesStore], ["newTasksFromTemplates", NewProjectTasksFromTemplates], ["newProjectTaskAssignment", NewProjectTaskAssignmentStore])
class ApplyTemplateView extends React.Component<ApplyTemplateViewProps, ApplyTemplateViewState> {

    private get TaskTemplatesStore() {
        return (this.props as any).taskTemplates as TaskTemplatesStore;
    }

    private get NewTaskFromTemplatesStore() {
        return (this.props as any).newTasksFromTemplates as NewProjectTasksFromTemplates;
    }

    private get newProjectTaskAssignmentStore() {
        return (this.props as any).newProjectTaskAssignment as NewProjectTaskAssignmentStore;
    }

    constructor(props: ApplyTemplateViewProps) {
        super(props);
        this.state = {
            taskTemplatesQuery: { searchQuery: '', orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }], skip: 0, take: 10 },
            selected: [],
            isBusy: false,
            selectedRoles: [],
            employeesIds: []
        }
    }

    componentWillMount() {
        this.loadTasks()
    }

    @autobind
    private async onCreateNewTasks() {
        this.setState({ isBusy: true });

        const selectedRoles = this.state.selectedRoles;
        const item = {
            taskTemplateIds: this.state.selected,
            selectedRoles: selectedRoles,
        };

        try {
            await this.NewTaskFromTemplatesStore.change(item);
            await this.NewTaskFromTemplatesStore.changeUrl(this.props.companyId, this.props.projectId);

            const result = await this.NewTaskFromTemplatesStore.postList();

            if ((result.data as any).success) {
                const taskTemplateIds = (result.data as any).projectTasks;
                const employeesIds = (result.data as any).employeesIds;

                if (employeesIds.length > 0 && taskTemplateIds.length > 0) {
                    const newItem = {
                        employeeId: '00000000-0000-0000-0000-000000000000',
                        projectTaskId: taskTemplateIds,
                        projectId: this.props.projectId,
                        templateEmployeeId: employeesIds,
                    } as NewProjectTaskAssignmentItem;

                    await this.newProjectTaskAssignmentStore.changeUrl(this.props.companyId, this.props.projectId);
                    await this.newProjectTaskAssignmentStore.createNew(newItem);
                    await this.newProjectTaskAssignmentStore.submit();
                }

                this.NewTaskFromTemplatesStore.clear();
                this.props.onClose();
                return result;
            } else {
                throw new Error("La solicitud falló");
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.setState({ isBusy: false });
        }
    }

    private handleRoleSelectionChange = (selectedRoles: string[]) => {
        this.setState({ selectedRoles });
    };

    @autobind
    private onCancelNewItem() {
        this.props.onClose()
    }

    @autobind
    private loadTasks() {

        this.TaskTemplatesStore.changeUrl(this.props.companyId)
        this.TaskTemplatesStore.getAllAsync(this.state.taskTemplatesQuery);
    }

    @autobind
    private onTaskQueryChanged(q: Query) {
        this.setState({ taskTemplatesQuery: q }, () => this.loadTasks());
    }

    @autobind
    private changeQueryByProjectFilter(projectIds: string[] | string) {
        this.setState({
            taskTemplatesQuery: {
                ...this.state.taskTemplatesQuery,
                parameters: {
                    ...this.state.taskTemplatesQuery.parameters,
                    'projectTemplateIds': projectIds
                }
            }
        }, () => this.loadTasks())
    }

    @autobind
    private taskTemplateListRender() {
        const { t } = this.props
        const tableModel = {
            query: this.state.taskTemplatesQuery,
            columns: [
                {
                    sortable: true,
                    field: 'title',
                    title: t('Title'),
                    renderer: (data) => (
                        <span> {data.title} </span>
                    ),
                },
                {
                    sortable: true,
                    field: 'reference',
                    title: t('Reference'),
                    renderer: (data) => (
                        <span> {data.reference} </span>
                    ),
                },
                {
                    sortable: false,
                    field: 'sticky',
                    title: t('Sticky'),
                    renderer: (data) => (
                        <span>{data.sticky && <Icon type="check" />} </span>
                    ),
                },
                {
                    sortable: false,
                    field: 'ciiEligibility',
                    title: t('CII Eligibility'),
                    renderer: (data) => (
                        <span> {data.ciiEligibility && <Icon type="check" />} </span>
                    ),
                },
                {
                    sortable: false,
                    field: 'cirEligibility',
                    title: t('CIR Eligibility'),
                    renderer: (data) => (
                        <span style={{ textAlign: "center" }}>{data.cirEligibility && <Icon type="check" />} </span>
                    ),
                },
                {
                    sortable: false,
                    field: 'projectTemplates',
                    title: t('Project Templates'),
                    renderer: (data) => (
                        <ProjectTemplatesColumn projectTemplates={data.projectTemplates} />
                    ),
                }
            ],
            data: this.TaskTemplatesStore.state,
            sortFields: [{
                field: 'title',
                text: t('Title'),
                useProfile: false
            }],
        } as TableModel<TaskTemplateSummary>;
        return (
            <>
                <Spin size='large' spinning={this.TaskTemplatesStore.state.isBusy || this.newProjectTaskAssignmentStore.state.isBusy}>
                    <TableView
                        rowKey='id'
                        model={tableModel}
                        error={this.TaskTemplatesStore.state.result && !this.TaskTemplatesStore.state.result.isSuccess && formatMessage(this.TaskTemplatesStore.state.result)}
                        embedded={false}
                        onQueryChanged={(q: Query) => this.onTaskQueryChanged(q)}
                        onRefresh={() => this.loadTasks()}
                        canEdit={false}
                        canDelete={false}
                        canCreateNew={false}
                        onSelectedChange={(rowKeys: any[]) => this.setState({ selected: rowKeys })}
                        leftToolbar={
                            <div style={{ alignItems: 'center', width: 570, justifyContent: 'space-between' }}>
                                <div style={{ marginRight: 'auto' }}>
                                    <ProjectTemplatesFilter
                                        companyId={this.props.companyId}
                                        multiple
                                        placeholder={t('Project Template')}
                                        onChange={this.changeQueryByProjectFilter}
                                    />
                                </div>
                                <div style={{ marginRight: '16px', paddingBottom: 10, marginTop: '10px' }}>
                                    <Select
                                        mode="multiple"
                                        placeholder={t('Select roles')}
                                        style={{ width: 570 }}
                                        value={this.state.selectedRoles}
                                        onChange={this.handleRoleSelectionChange}
                                    >
                                        {roles.sort().map((role) => (
                                            <Select.Option key={role} value={role}>
                                                {role}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        }
                    >
                    </TableView>
                </Spin>
            </>
        )
    }

    public render() {

        const { t } = this.props;
        return (
            <Modal
                maskClosable={false}
                visible
                onCancel={this.onCancelNewItem}
                onOk={this.onCreateNewTasks}
                closable={false}
                width='1000px'
                title={t("Apply Template")}>
                {this.NewTaskFromTemplatesStore.state.result && !this.NewTaskFromTemplatesStore.state.result.isSuccess &&
                    <Alert type='error'
                        message={t("Error")}
                        description={t(formatMessage(this.NewTaskFromTemplatesStore.state.result))}
                    />
                }
                <div style={{ minHeight: '50vh', overflowX: 'hidden', overflowY: 'auto' }}>
                    <Spin spinning={this.NewTaskFromTemplatesStore.state.isBusy || this.state.isBusy}>
                        {this.taskTemplateListRender()}
                    </Spin>
                </div>
            </Modal>
        );
    }
}

// get a map of { id (string) : isSelected (boolean) }
const getMapFromItems = (items: string[], getValue?: (idx: string) => boolean) => {
    var a: { [k: string]: boolean } = {}
    for (let i of items) {
        a[i] = getValue ? getValue(i) : false
    }
    return a
}

// Wire up the React component to the Redux store
export default withTranslation()(ApplyTemplateView);